import { Check, ChevronDown, Eye, ShieldCheck, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAppView } from '@/contexts/AppViewContext';
import type { AppViewMode } from '@/lib/appAccess';

const viewOptions: Array<{
  mode: AppViewMode;
  label: string;
  description: string;
  icon: typeof ShieldCheck;
}> = [
  {
    mode: 'elevated',
    label: 'Elevated view',
    description: 'Full Compliance Magic workspace',
    icon: ShieldCheck,
  },
  {
    mode: 'standard',
    label: 'Standard view',
    description: 'Dashboard, domain tools, and ATBS',
    icon: Users,
  },
];

export function ViewModeSwitcher() {
  const { canPreviewViews, viewMode, setViewMode } = useAppView();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  if (!canPreviewViews) return null;

  const isStandard = viewMode === 'standard';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`inline-flex min-h-10 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
          isStandard
            ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 focus-visible:ring-amber-500'
            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 focus-visible:ring-blue-500'
        }`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`View as: ${isStandard ? 'standard user' : 'elevated admin'}`}
      >
        <Eye className="h-4 w-4" aria-hidden="true" />
        <span className="hidden lg:inline">View:</span>
        <span>{isStandard ? 'Standard' : 'Elevated'}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Choose app view"
          className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
        >
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-slate-900">View as</p>
            <p className="mt-0.5 text-xs text-slate-500">Preview only — your Level 3 permissions stay unchanged.</p>
          </div>
          <div className="p-2">
            {viewOptions.map((option) => {
              const Icon = option.icon;
              const selected = option.mode === viewMode;
              return (
                <button
                  key={option.mode}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  onClick={() => {
                    setViewMode(option.mode);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors ${
                    selected ? 'bg-blue-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <span className={`mt-0.5 rounded-md p-1.5 ${selected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-900">{option.label}</span>
                    <span className="mt-0.5 block text-xs text-slate-500">{option.description}</span>
                  </span>
                  {selected && <Check className="mt-1 h-4 w-4 text-blue-700" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
